import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { applySkinToneToText } from '../../../../utils/emoji';
import colors from '../../../../styles/colors';
import { ChatChannelContext, MessageInputContext } from '../../../context';
import {
  MultiResponseContainer,
  MultiResponseItem,
  MultiResponseInput,
  ReplyMessage,
  ReplyMessageInternalTextWarning,
  ButtonsContainer,
  StyledActionButton,
  InsertButton,
  ScheduleQueueButton,
  InputContainer,
  InputLabel,
  StyledDivider,
} from './styles';
import texts from './texts.json';

const MultiResponsePreview = ({
  multiResponses,
  additionalQueryInSR,
  onInsertClick,
  onCopyClick,
  removeResponse,
  traceIdInSR,
  onScheduleClick,
  scheduledMessageIds,
}) => {
  const { setReply } = useContext(MessageInputContext);
  const { messages, coachDoc } = useContext(ChatChannelContext);

  const handleReply = (messageId) => {
    const message = messages.find((msg) => msg.id === messageId);

    if (message) {
      setReply(message);
    }
  };

  const sortedMultiResponses = useMemo(() => [...multiResponses]
    .map(({
      messageId, response, originalMessage, createdAt,
    }) => ({
      messageId,
      response,
      originalMessage,
      createdAt: createdAt ? new Date(createdAt) : new Date(0),
    }))
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
  [multiResponses]);

  return (
    <MultiResponseContainer>
      {sortedMultiResponses.map(({ messageId, response, originalMessage }) => {
        const isQueued = scheduledMessageIds.includes(messageId);
        return (
          <MultiResponseItem key={messageId}>
            <InputContainer>
              <InputLabel>{texts.clientsMessage}</InputLabel>
              <MultiResponseInput>
                <ReplyMessage>
                  {originalMessage || (
                    <ReplyMessageInternalTextWarning>
                      {texts.noClientsMessage}
                    </ReplyMessageInternalTextWarning>
                  )}
                </ReplyMessage>
                <StyledDivider />
                {applySkinToneToText(response, coachDoc?.emojisSkinTone)}
              </MultiResponseInput>
            </InputContainer>

            <ButtonsContainer>
              <InsertButton
                padding="8px"
                backgroundColor={colors.base.secondary2}
                onClick={() => {
                  handleReply(messageId);
                  onInsertClick(
                    applySkinToneToText(response, coachDoc?.emojisSkinTone),
                    additionalQueryInSR,
                    true,
                    traceIdInSR,
                  );
                  removeResponse(messageId);
                }}
              >
                {texts.insertButton}
              </InsertButton>
              <ScheduleQueueButton
                $queued={isQueued}
                onClick={() => onScheduleClick(
                  applySkinToneToText(response, coachDoc?.emojisSkinTone),
                  messageId,
                )}
              >
                {isQueued ? texts.queuedButton : texts.scheduleButton}
              </ScheduleQueueButton>
              <StyledActionButton onClick={() => onCopyClick(response)}>
                {texts.copyButton}
              </StyledActionButton>
            </ButtonsContainer>
          </MultiResponseItem>
        );
      })}
    </MultiResponseContainer>
  );
};

MultiResponsePreview.defaultProps = {
  additionalQueryInSR: '',
  traceIdInSR: '',
  onScheduleClick: () => {},
  scheduledMessageIds: [],
};

MultiResponsePreview.propTypes = {
  multiResponses: PropTypes.object.isRequired,
  additionalQueryInSR: PropTypes.string,
  onInsertClick: PropTypes.func.isRequired,
  onCopyClick: PropTypes.func.isRequired,
  removeResponse: PropTypes.func.isRequired,
  traceIdInSR: PropTypes.string,
  onScheduleClick: PropTypes.func,
  scheduledMessageIds: PropTypes.arrayOf(PropTypes.string),
};

export default observer(MultiResponsePreview);
